import { Component, ViewChild } from '@angular/core';
import { HeaderService } from '@app/core/services/header.service';
import { ModalService } from '@app/core/services/modal.service';
import { DashboardService } from '@app/api/dmc';
import { TableConfig } from '@shared/interfaces/table/table-config';
import { queryDashboardConfig, QueryDashboardConfig } from '@views/dashboard/config/query.dashboard-config';
import { TableComponent } from '@shared/components/table/table.component';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { Observable, of } from 'rxjs';
import { SortParam } from '@app/core/constants/sort-param';
import { ToggleComponent } from '@shared/components/input/toggle/toggle.component';
import { FormGroup, FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-dashboard-query-handler',
  imports: [
    TableComponent,
    ToggleComponent,
    TranslatePipe,
    ReactiveFormsModule
  ],
  templateUrl: './dashboard-query-handler.component.html',
  styleUrl: './dashboard-query-handler.component.css'
})
export class DashboardQueryHandlerComponent {
  @ViewChild('table', {static: false}) public table?: TableComponent<any>;

  public tableConfig?: TableConfig<any>
  public tableData: any[] = [];
  public selectedElements: any[] = [];

  private activeConfig?: QueryDashboardConfig;
  private readonly availableConfigs: { [key: string]: QueryDashboardConfig } = queryDashboardConfig;

  public queryKey!: string;
  public showToggle: boolean = false;
  public toggleLabel: string = '';
  public activeToggleControlName: 'processedRequests' | 'showHandled' = 'processedRequests';

  public form: FormGroup = new FormGroup({
    processedRequests: new FormControl(false),
    showHandled: new FormControl(false),
  });

  readonly processedToggleIds = [
    'QueryDisconnect',
    'QueryDisconnectDueToEndOfSupply',
    'QueryReopen',
    'QueryCloseDown'
  ];
  readonly handledToggleIds = [
    'QueryRejectedMeteringPoints'
  ];

  constructor(
    private readonly headerService: HeaderService,
    private readonly modalService: ModalService,
    private readonly dashboardService: DashboardService,
    private readonly route: ActivatedRoute,
  ) {
    this.route.paramMap.subscribe((q: ParamMap) => {
      this.queryKey = q.get('query')!;

      this.route.queryParams.subscribe(qp => {
        const processed = qp['processedRequests'] === 'true';
        this.form.get('processedRequests')!.setValue(processed, {
          emitEvent: false
        });
        const showHandled = qp['showHandled'] === 'true';
        this.form.get('showHandled')!.setValue(showHandled, {
          emitEvent: false
        });
      });


      /** Fetch Config by route key **/
      this.fetchConfig(this.queryKey);

      if (this.activeConfig?.id &&
        this.processedToggleIds.includes(this.activeConfig.id)) {
        this.showToggle = true;
        this.activeToggleControlName = 'processedRequests';
        this.toggleLabel = 'dashboard.servicerequest.complete.displayprocessed';
      } else if (this.activeConfig?.id &&
        this.handledToggleIds.includes(this.activeConfig.id)) {
        this.showToggle = true;
        this.activeToggleControlName = 'showHandled';
        this.toggleLabel = 'dashboard.timeseries.missing.showHandled';
      } else {
        this.showToggle = false;
      }

      /** Define Header **/
      this.headerService.setTitle(`dashboard.query.${this.queryKey}.title`);

      if (this.activeConfig?.menuActions) {
        const modalReference: Record<string, () => void> = {};
        const activeReference: Record<string, () => boolean> = {};

        if (this.activeConfig.modals) {
          Object.keys(this.activeConfig.modals).forEach((modalKey: string) => {
            modalReference[modalKey] = () => this.triggerModal(modalKey);
            if (this.activeConfig!.modals![modalKey].activeLength) {
              activeReference[modalKey] = () =>
                this.selectedElements.length !== this.activeConfig!.modals![modalKey].activeLength ||
                this.handledActionIsDisabled(modalKey);
            }
          });
        }
        this.headerService.setActions(this.activeConfig.menuActions,
          modalReference,
          activeReference,
        );
      }
    });

    // Subscribe to toggle changes
    this.form.get('processedRequests')!.valueChanges.subscribe(() => {
      if (this.activeConfig?.serviceCall) {
        this.activeConfig.serviceCall.params = {
          ...this.activeConfig.serviceCall.params,
          processedRequests: this.form.get('processedRequests')!.value
        };
        this.table?.search();
      }
    });

    this.form.get('showHandled')!.valueChanges.subscribe(() => {
      if (this.activeConfig?.serviceCall) {
        this.activeConfig.serviceCall.params = {
          ...this.activeConfig.serviceCall.params,
          showHandled: this.form.get('showHandled')!.value
        };
        this.table?.search();
      }
    });
  }
  
  /** Fetch Config by route key **/
  private fetchConfig(key: string): void {
    if (this.availableConfigs[key]) {
      this.activeConfig = this.availableConfigs[key];

      // Map menuActions to include callbacks that trigger modals
      const contextActions = this.activeConfig.menuActions?.map(action => ({
        ...action,
        callback: () => this.triggerModal(action.id),
        disabled: () => {
          const modal = this.activeConfig?.modals?.[action.id];
          if (modal?.activeLength) {
            return this.selectedElements.length !== modal.activeLength ||
              this.handledActionIsDisabled(action.id);
          }
          return this.handledActionIsDisabled(action.id);
        }
      }));


      if (this.activeConfig.serviceCall) {
        this.refreshServiceCallParams();
      }

      this.tableConfig = {
        ...this.activeConfig.tableConfig,
        serviceCallback: (pageSize: number, page: number, sort?: SortParam) => this.search(pageSize, page, sort),
        contextActions: contextActions,
      }
    }
  }

  /** Trigger Modal **/
  private triggerModal(modalKey: string): void {
    if (this.handledActionIsDisabled(modalKey)) {
      return;
    }

    if (!this.activeConfig?.modals || !this.activeConfig.modals[modalKey]) {
      console.warn(`Modal ${modalKey} not found in config`);
      return;
    }

    this.modalService.show({
      ...this.activeConfig.modals[modalKey].modalConfig,
      params: {
        ...(this.activeConfig.modals[modalKey].params),
        element: this.selectedElements[0],
        elements: this.selectedElements
      }
    }).subscribe(() => {
      this.table?.search();
    });
  }

  private handledActionIsDisabled(actionId: string): boolean {
    return actionId === 'manual' &&
      this.handledToggleIds.includes(this.activeConfig?.id ?? '') &&
      this.form.get('showHandled')!.value === true;
  }

  /** Parsed Search for Paginiation **/
  public search(pageSize: number, page: number, sort?: SortParam): Observable<any> {
    if (!this.activeConfig?.serviceCall) {
      return of();
    }

    const method = this.activeConfig.serviceCall.method;
    this.refreshServiceCallParams();

    const params = {
      ...this.activeConfig.serviceCall.params,
      page,
      pageSize,
    };

    if (typeof this.dashboardService[method] === 'function') {
      return (this.dashboardService[method] as Function)(...this.getParameterOrder(params).map(parameter => params[parameter]))
    }

    console.error(`Method ${method} not found in DashboardService`);
    return of();
  }

  private refreshServiceCallParams(): void {
    if (!this.activeConfig?.serviceCall) {
      return;
    }

    this.activeConfig.serviceCall.params = {
      ...this.activeConfig.serviceCall.params,
      ...(this.processedToggleIds.includes(this.activeConfig.id ?? '')
        ? { processedRequests: this.form.get('processedRequests')!.value }
        : {}),
      ...(this.handledToggleIds.includes(this.activeConfig.id ?? '')
        ? { showHandled: this.form.get('showHandled')!.value }
        : {}),
    };
  }

  private getParameterOrder(params: Record<string, unknown>): string[] {
    return this.activeConfig?.serviceCall?.parameterOrder
      ?? [
        ...(Object.hasOwn(params, 'processedRequests') ? ['processedRequests'] : []),
        ...(Object.hasOwn(params, 'showHandled') ? ['showHandled'] : []),
        'page',
        'pageSize',
      ];
  }
}
